import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { ConfigProvider } from 'antd';
import { ConnectedRouter } from 'connected-react-router';
import { Route, Switch, Redirect } from 'react-router-dom';
import DocumentTitle from 'react-document-title';
import qs from 'querystring';
import moment from 'moment';
import { hot } from 'react-hot-loader/root';
import { throttle } from 'lodash';
import {callNativeAsyncFunc} from './native';

import DailyReport from 'src/routes/statistics';
import { $t } from './tools';
import { setStorage, getStorage, STORAGE } from './tools/storage';
import { SUPPORT_LANG, langs } from './assets/lang.json';
import routes from './routes/router';
import BasicLayout from './layouts/BasicLayout';
import AuthRoute from './components/AuthRoute';
import { isMC } from './utils/env';

let timer = null;

const App = (props) => {
  const {
    dispatch,
    history,
    history: {
      location: { pathname },
    },
    appState: {
      userInfo: { role },
      token,
      locale,
      langMap,
      reportList,
      maskToken,
    },
    loginState: { loading },
  } = props;
  // 判断是否来自网页 newPro
  let fromNpWeb = window.name.includes('newPro') && !window.name.includes('native_');
  let fromNpNative = window.name.includes('newPro') && window.name.includes('native_');
  if (fromNpWeb || fromNpNative) {
    const getUserData = () => {
      callNativeAsyncFunc({ ns: 'airWeb', fn: 'userData' }, { token: token, maskToken: maskToken } ).then(r =>{
            const backToken = r?.token ?? '';
            const backMasksToken = r?.maskToken ?? '';
            if (backToken && backMasksToken && backToken != '' && backMasksToken != '' && (backToken !== token || backMasksToken !== maskToken)) {
              dispatch({
                type: 'app/setState',
                payload:{token:backToken,maskToken:backMasksToken}
              });
              dispatch({
                type: 'app/syncAppData',
              });
            }
      })
    };
    getUserData();
    function moveWheel(){
      window.parent.postMessage(JSON.stringify({ name: 'move_wheel' }), '*');
    }
    document.addEventListener('wheel',moveWheel,false);
    document.addEventListener('touchmove',moveWheel,false);
  }else{
    const _token = getStorage(STORAGE.ADMIN_TOKEN)
    const _maskToken = getStorage(STORAGE.ADMIN_MASK_TOKEN)
    if(_token && _maskToken && (_token !== token || _maskToken !== maskToken)){
      dispatch({
        type: 'app/setState',
        payload:{token:_token,maskToken:_maskToken}
      });
      dispatch({
        type: 'app/syncAppData',
      });
    }
  }

  useEffect(() => {
    handleLang();
  }, [locale]);

  useEffect(() => {
    // 用户没有勾选"保持登录",且没有进行任何操作时，执行五分钟后自动跳转到登录界面
    const keepLogin = getStorage(STORAGE.SYSTEM_KEEP_LOGIN);
    if (!keepLogin && token && !(fromNpWeb || fromNpNative)) {
      autoExit();
    }
    // 是否可报表详情路由
    const reportRouteRex = /\/v2\/statistics\/[0-9]*$/;
    const reportRoutePermissions = reportRouteRex.test(pathname);
    if (token && !getRoute().includes(pathname) && !reportRoutePermissions && !loading) {
      dispatch({ type: 'login/redirect' });
    }
    return () => {
      clearTimeout(timer);
      document.onmousemove = null;
    };
  });

  // 设置网页标题
  const prefixTitle = (title) => {
    return `${$t(title, langMap[locale]?.Sider)} - ${process.env.WEBSITE_DEFAULT_TITLE}`;
  };

  // 获取当前角色可访问的路由
  const getRoute = () => {
    const routeList = routes.reduce((acc, item) => {
      if (item.children) {
        item.children.forEach((c) => {
          if (!c.authority || c.authority.includes(role)) {
            acc.push(c.path);
          }
        });
      } else {
        acc.push(item);
      }
      return acc;
    }, []);
    return routeList;
  };

  // 自动退出
  const autoExit = () => {
    const timeout = 1000 * 60 * 5;
    timer = setTimeout(() => {
      dispatch({ type: 'login/logout' });
    }, timeout);
    document.onmousemove = throttle(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        dispatch({ type: 'login/logout' });
      }, timeout);
    }, 1000);
  };

  // 多语言相关处理
  const handleLang = () => {
    const momentLang = {
      chs: 'zh-cn',
      en: 'en-gb',
    };
    dispatch({
      type: 'app/setState',
      payload: {
        langMap: langs,
      },
    });
    if (isMC()) {
      const { lang } = qs.parse(window.location.search.slice(1));
      // 路由语言 > localStorage存的语言 > 浏览器默认语言 > 英文
      const curLang =
        (lang || '').replace(/\//, '') ||
        getStorage('mc-locale') ||
        (navigator.language || navigator.userLanguage || '').slice(0, 2).replace('zh', 'chs') ||
        'en';
      const langArr = Object.keys(SUPPORT_LANG);
      if (
        curLang &&
        langArr.includes(curLang) &&
        (locale !== curLang || !langArr.includes(locale))
      ) {
        dispatch({
          type: 'app/setState',
          payload: { locale: curLang },
        });
      }
      moment.locale(momentLang[curLang] || curLang);
      setStorage(STORAGE.LOCALE.MAP, langMap[curLang], false);
      dispatch({
        type: 'app/setState',
        payload: {
          defaultTime: 0,
        },
      });
      dispatch({ type: 'app/fetchLangFile' });
    } else {
      const lang = 'chs';
      setStorage(STORAGE.LOCALE.MAP, langMap[lang], false);
      dispatch({
        type: 'app/setState',
        payload: { locale: lang },
      });
    }
  };

  const curLangs = langMap[locale] || {};
  return (
    <ConfigProvider locale={curLangs}>
      <ConnectedRouter history={history}>
        <Switch>
          {routes.map((route) =>
            route.redirect ? (
              <Redirect key={route.path} to={{ pathname: route.redirect }} />
            ) : route.auth ? (
              <Route
                key={route.path}
                path={route.path}
                render={(props) => (
                  <BasicLayout {...props} fromNp={fromNpWeb || fromNpNative}>
                    {route.children ? (
                      role ? (
                        route.children
                          .filter((i) => i.authority?.includes(role))
                          .map((sub) => {
                            if (sub.children) {
                              sub.children = sub.children.filter((i) => i.authority.includes(role));
                            }
                            return sub;
                          })
                          .map((sub) => (
                            <AuthRoute
                              {...props}
                              key={sub.path}
                              path={sub.path}
                              redirect='/login'
                              checker={Boolean(token)}
                              render={(props) => {
                                return (
                                  <DocumentTitle title={prefixTitle(sub.title)}>
                                    <sub.component
                                      locales={curLangs[sub.name] || curLangs[sub.key]}
                                      {...props}
                                    />
                                  </DocumentTitle>
                                );
                              }}
                            />
                          ))
                      ) : (
                        route.children.map((sub) => (
                          <AuthRoute
                            {...props}
                            key={sub.path}
                            path={sub.path}
                            redirect='/login'
                            checker={Boolean(token)}
                            render={(props) => {
                              return (
                                <DocumentTitle title={prefixTitle(sub.title)}>
                                  <sub.component
                                    locales={curLangs[sub.name] || curLangs[sub.key]}
                                    {...props}
                                  />
                                </DocumentTitle>
                              );
                            }}
                          />
                        ))
                      )
                    ) : (
                      <DocumentTitle title={prefixTitle(route.title)}>
                        <route.component locales={curLangs[route.name]} {...props} />
                      </DocumentTitle>
                    )}
                    {reportList.map((v, i) => (
                      <AuthRoute
                        key={`/v2/statistics/${v.key || v.id}`}
                        path={`/v2/statistics/${v.key || v.id}`}
                        redirect='/login'
                        checker={Boolean(token)}
                        render={(props) => {
                          return (
                            <DocumentTitle title={prefixTitle(v.name)}>
                              <DailyReport type={String(v.key || v.id || i)} {...props} />
                            </DocumentTitle>
                          );
                        }}
                      />
                    ))}
                  </BasicLayout>
                )}
              />
            ) : (
              <Route
                exact
                key={route.path}
                path={route.path}
                render={(props) => (
                  <DocumentTitle title={prefixTitle(route.title)}>
                    <route.component locales={curLangs[route.name]} {...props} />
                  </DocumentTitle>
                )}
              />
            ),
          )}
        </Switch>
      </ConnectedRouter>
    </ConfigProvider>
  );
};

export default hot(connect(({ loginState, appState }) => ({ loginState, appState }))(App));
